import { getResources } from "../../getResources";
import { createAdaptiveCard, getHTMLElementFromCard, getVisualThemeString } from "./AdaptiveCardRenderingAndActions";
import tombstoneDataFallback from "./tombstoneResources/tombstoneData.json";
import tombstoneTemplate from "./tombstoneResources/tombstoneTemplate.json";
import signedOutTodoDataFallback from "./outlookSignOutResources/outlookSignoutToDoData.json";
import signedOutCalendarDataFallback from "./outlookSignOutResources/outlookSignoutCalendarData.json";
import signedOutTemplate from "./outlookSignOutResources/signOutOutlookTemplate.json";
import * as Constants from "../../constants";
// Edge protocol can also be used to launch the Store page if Edge is uninstalled.
// Using the product id to make testing possible for all builds/branches/rings.
const launchLinkEdgeStable = "ms-windows-store://pdp?productid=XPFFTQ037JWMHS";
const launchLinkPrefix = "ms-windows-store://pdp/?PFN=";
let savedTombstoneTemplate = "";
let savedSignedOutTemplate = "";
// This class is to get the Signed UI and Tombstone UI template and data, which are stored locally.
export async function getLocalAdaptiveCard(instance, height, width) {
    let cardDiv;
    let cardError = "none";
    let template = "";
    let data = "";
    let dataError = "none";
    if (instance.widgetRegistration.isTombstoned) {
        template = getTombstoneTemplate();
        [data, dataError] = await getTombstoneData(instance);
    }
    else if (instance.isOutlookWidget()) {
        template = getSignedOutTemplate();
        [data, dataError] = await getSignedOutData(instance);
    }
    else {
        return [document.createElement("div"), "invalidInstance", dataError];
    }
    let hostContext = {};
    hostContext.hostTheme = await getVisualThemeString();
    hostContext.isSettingsPayload = "false";
    hostContext.widgetSize = instance.getWidgetProperty("size");
    const config = { data: data, hostContext: hostContext, template: template };
    let card = undefined;
    let error = "none";
    [card, error] = createAdaptiveCard(instance, config);
    if (card !== undefined && error === "none") {
        cardDiv = await getHTMLElementFromCard(card, instance, height, width);
    }
    else {
        // This should never happen because our template/data never change
        // If card is undefined, "AdaptiveCardCreationFailed" will fire
        return [document.createElement("div"), "cardUndefined", dataError];
    }
    if (cardDiv === undefined) {
        // This should never happen because a cardDiv is always returned but log telemetry in unexpected case
        return [document.createElement("div"), "cardDivUndefined", dataError];
    }
    return [cardDiv, cardError, dataError];
}
function getSignedOutTemplate() {
    if (savedSignedOutTemplate === "") {
        savedSignedOutTemplate = JSON.stringify(signedOutTemplate);
    }
    return savedSignedOutTemplate;
}
async function getSignedOutData(widgetInstance) {
    let message = "";
    let error = "none";
    if (widgetInstance.widgetDefinitionId === Constants.CalendarWidgetId) {
        message = getResources().Outlook_Calendar_Signed_Out_Text;
    }
    else if (widgetInstance.widgetDefinitionId === Constants.ToDoWidgetId) {
        message = getResources().Outlook_ToDo_Signed_Out_Text;
    }
    if (message === "") {
        let fallbackData = widgetInstance.widgetDefinitionId === "com.microsoft.calendar" ? signedOutCalendarDataFallback : signedOutTodoDataFallback;
        return [JSON.stringify(fallbackData), "signedOutDataFailure"];
    }
    let signedOutData = {
        "signInText": message
    };
    return [JSON.stringify(signedOutData), error];
}
function getTombstoneTemplate() {
    if (savedTombstoneTemplate === "") {
        savedTombstoneTemplate = JSON.stringify(tombstoneTemplate);
    }
    return savedTombstoneTemplate;
}
async function getTombstoneData(widgetInstance) {
    /* eslint-disable @typescript-eslint/dot-notation */
    let tombstoneData = {};
    let error = "none";
    const localizedMessage = getResources().Tombstone_Text;
    tombstoneData["message"] = localizedMessage;
    // If the widget is a platform widget i.e. the backing app is a PWA, parse the appId to get PFN and construct Store link.
    // Use the hardcoded Store page link if not a platform widget. Win32 apps are not supported for Store PFN deeplink.
    if (widgetInstance.widgetRegistration.backingAppPFN === "Microsoft.MicrosoftEdge.Stable_8wekyb3d8bbwe") {
        if (widgetInstance.isPlatform) {
            let backingAppPFN = widgetInstance.widgetRegistration.appId.substring(0, widgetInstance.widgetRegistration.appId.indexOf("!"));
            tombstoneData["launchLink"] = launchLinkPrefix + backingAppPFN;
        }
        else {
            tombstoneData["launchLink"] = launchLinkEdgeStable;
        }
    }
    // No icon will appear if there is an issue with fetching the icon
    tombstoneData["icon"] = await getIconDataURI(widgetInstance);
    // Return fallback data if any value is empty
    if (Object.keys(tombstoneData).every((key) => !tombstoneData[key])) {
        error = "tombstoneDataFailure";
        tombstoneData = tombstoneDataFallback;
    }
    return [JSON.stringify(tombstoneData), error];
}
async function getIconDataURI(widgetInstance) {
    const propertiesApi = widgetInstance.tryGetApi("properties", 1, 1);
    let dataURI = "";
    if (propertiesApi) {
        let uri = propertiesApi.getProperty("widgetIconUri");
        if (uri.startsWith("data:")) {
            return uri;
        }
        else {
            await fetch(uri)
                .then((response) => response.text())
                .then((text) => {
                const base64 = btoa(text);
                dataURI = "data:image/svg+xml;base64," + base64;
            });
        }
    }
    return dataURI;
}
